import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Check, Loader2, ShieldCheck, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { ProgrammeCard, StatusBadge } from "@/components/learning/ProgrammeCard";
import { api, formatApiError } from "@/lib/api";
import { openCheckout } from "@/lib/payments";
import { trackMetaEvent } from "@/lib/meta";
import { STATUS_META } from "@/lib/learning";
import { useActiveMembership, useTrainingDiscount } from "@/hooks/useActiveMembership";
import MembershipGateNotice from "@/components/membership/MembershipGateNotice";

const inputCls = "w-full bg-transparent border-b border-ptf-primary/25 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";
const Eyebrow = ({ children }) => (
  <p className="text-[0.7rem] uppercase tracking-[0.28em] mb-4 flex items-center gap-3 text-ptf-primary/60">
    <span className="h-px w-8 inline-block bg-ptf-primary/40" />{children}
  </p>
);

function NotifyMeForm({ programme }) {
  const { member, hasActive, isLoading } = useActiveMembership();
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  if (isLoading) return <Loader2 className="h-5 w-5 animate-spin text-ptf-primary" />;
  if (!member) return <MembershipGateNotice member={member} isLoading={isLoading} testid="training-notify-gate" />;

  const notify = async () => {
    setLoading(true);
    try {
      await api.post("/member/training/notify-me", { programme_slug: programme.slug });
      setSent(true);
    } catch (err) {
      toast.error(err?.response ? (formatApiError(err.response.data?.detail) || "Could not save your request.") : "Could not save your request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="notify-me-form" className="border border-ptf-primary/12 bg-white p-7">
      <p className="text-sm text-ptf-text/70 leading-relaxed mb-5">
        This programme is coming soon and not yet open for booking.{" "}
        {hasActive ? "As an active Foundation member, we'll email you the moment it opens." : "Membership is required to book once it opens - but you can register for a notification now."}
      </p>
      {sent ? (
        <p data-testid="notify-me-confirmed" className="text-sm font-semibold text-ptf-secondary">You're on the list - we'll email {member.email} when this opens.</p>
      ) : (
        <button type="button" data-testid="notify-me-submit" disabled={loading} onClick={notify} className="bg-ptf-primary text-ptf-bg px-10 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60 inline-flex items-center gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Notify Me
        </button>
      )}
    </div>
  );
}

const PACKAGE_ORDER = ["course_only", "homestay", "premium"];

function RegistrationForm({ programme }) {
  const { member, hasActive, isLoading } = useActiveMembership();
  const { eligible: discountEligible, percent: discountPercent, source: discountSource } = useTrainingDiscount();
  const adventure = programme.category === "adventure-outdoor" || ["LAND", "WATER", "MOUNTAIN", "AIR"].includes(programme.terrain);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "India", city: "", age: "", emergency_contact: "",
    preferred_date: "", experience_level: "", relevant_experience: "", medical_info: "", dietary_info: "",
    special_requirements: "", heard_from: "", swimming_experience: "", outdoor_experience: "",
    trekking_experience: "", paddling_experience: "", certifications: "",
  });
  const [volunteerInterest, setVolunteerInterest] = useState(false);
  const [consent, setConsent] = useState(false);
  const [editionId, setEditionId] = useState("");
  const [packageKey, setPackageKey] = useState(programme.packages?.[0]?.key || "");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (member) {
      setForm((f) => ({ ...f, name: f.name || member.name || "", email: f.email || member.email || "", phone: f.phone || member.phone || "" }));
    }
  }, [member]);

  const regStatus = programme.registration_status || "OPEN";
  const paid = programme.status === "OFFERED" && programme.fee_type === "PAID" && Number(programme.fee) > 0 && ["", "OPEN"].includes(regStatus);
  const showDiscount = paid && discountEligible && programme.eligible_for_discount && programme.slug !== "the-last-horizon";
  const selectedPackage = programme.packages?.find((pk) => pk.key === packageKey);
  const baseFee = selectedPackage ? Number(selectedPackage.price) : Number(programme.fee);
  const displayFee = showDiscount ? Math.round(baseFee * (1 - discountPercent / 100)) : baseFee;

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please confirm the consent statement to submit."); return; }
    if (programme.packages?.length > 0 && !packageKey) { toast.error("Please choose a package option."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/training/register", {
        ...form, programme_slug: programme.slug, programme_title: programme.title,
        volunteer_interest: volunteerInterest, consent, edition_id: editionId, package_key: packageKey,
      });
      if (data.payment) {
        const resp = await openCheckout({
          keyId: data.key_id, orderId: data.order_id, amount: data.amount, currency: data.currency,
          description: programme.title,
          prefill: { name: form.name, email: form.email, contact: form.phone },
          tracking: { content_name: programme.title, content_type: "training", product_type: "training_programme", programme_id: programme.slug },
        });
        const v = await api.post("/training/register/verify", {
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
          fbp: resp.fbp, fbc: resp.fbc,
        });
        setDone(v.data.message);
        return;
      }
      setDone(data.message);
    } catch (err) {
      toast.error(err?.response ? (formatApiError(err.response.data?.detail) || "Could not send the registration.") : err.message || "Could not send the registration.");
    } finally {
      setLoading(false);
    }
  };

  if (isLoading || !hasActive) {
    return <MembershipGateNotice member={member} isLoading={isLoading} testid="training-membership-gate" />;
  }

  if (done) {
    return (
      <motion.div data-testid="registration-success" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="py-4">
        <span className="grid place-items-center h-12 w-12 rounded-full bg-ptf-accent text-ptf-primary mb-5"><Check className="h-6 w-6" /></span>
        <h3 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Received.</h3>
        <p className="mt-3 text-sm text-ptf-text/75 leading-relaxed max-w-md">{done}</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="registration-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <input required data-testid="reg-name" value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} />
        <input required type="email" data-testid="reg-email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
        <input data-testid="reg-phone" value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
        <input data-testid="reg-age" value={form.age} onChange={set("age")} placeholder="Age" className={inputCls} />
        <input data-testid="reg-country" value={form.country} onChange={set("country")} placeholder="Country" className={inputCls} />
        <input data-testid="reg-city" value={form.city} onChange={set("city")} placeholder="City" className={inputCls} />
        <input data-testid="reg-emergency" value={form.emergency_contact} onChange={set("emergency_contact")} placeholder="Emergency contact (name & phone)" className={inputCls} />
        <input data-testid="reg-date" value={form.preferred_date} onChange={set("preferred_date")} placeholder="Preferred date / month" className={inputCls} />
        <select data-testid="reg-level" value={form.experience_level} onChange={set("experience_level")} className={inputCls}>
          <option value="">Experience level</option>
          {["Complete beginner", "Some experience", "Experienced", "Professional"].map((l) => <option key={l}>{l}</option>)}
        </select>
        <input data-testid="reg-heard" value={form.heard_from} onChange={set("heard_from")} placeholder="How did you hear about us?" className={inputCls} />
      </div>
      <textarea data-testid="reg-experience" rows={2} value={form.relevant_experience} onChange={set("relevant_experience")} placeholder="Relevant experience" className={`${inputCls} resize-none`} />
      {adventure && (
        <div className="border border-ptf-primary/12 bg-ptf-primary/[0.03] p-5 space-y-4">
          <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-primary/60">Adventure programme details</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="reg-swimming" value={form.swimming_experience} onChange={set("swimming_experience")} placeholder="Swimming experience" className={inputCls} />
            <input data-testid="reg-outdoor" value={form.outdoor_experience} onChange={set("outdoor_experience")} placeholder="Outdoor experience" className={inputCls} />
            <input data-testid="reg-trekking" value={form.trekking_experience} onChange={set("trekking_experience")} placeholder="Previous trekking" className={inputCls} />
            <input data-testid="reg-paddling" value={form.paddling_experience} onChange={set("paddling_experience")} placeholder="Previous kayaking / rafting" className={inputCls} />
          </div>
          <input data-testid="reg-certs" value={form.certifications} onChange={set("certifications")} placeholder="Relevant certifications (if any)" className={inputCls} />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <input data-testid="reg-medical" value={form.medical_info} onChange={set("medical_info")} placeholder="Medical / safety information (optional)" className={inputCls} />
        <input data-testid="reg-dietary" value={form.dietary_info} onChange={set("dietary_info")} placeholder="Dietary information (optional)" className={inputCls} />
      </div>
      <input data-testid="reg-special" value={form.special_requirements} onChange={set("special_requirements")} placeholder="Special requirements (optional)" className={inputCls} />
      {programme.packages?.length > 0 && (
        <div className="space-y-2">
          <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-text/55">Choose your package</span>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="reg-package-options">
            {[...programme.packages].sort((a, b) => PACKAGE_ORDER.indexOf(a.key) - PACKAGE_ORDER.indexOf(b.key)).map((pk) => (
              <label key={pk.key} data-testid={`reg-package-${pk.key}`} className={`border p-4 cursor-pointer transition-colors ${packageKey === pk.key ? "border-ptf-secondary bg-ptf-secondary/[0.06]" : "border-ptf-primary/15"}`}>
                <input type="radio" name="package" value={pk.key} checked={packageKey === pk.key} onChange={() => setPackageKey(pk.key)} className="accent-ptf-secondary" />
                <span className="ml-2 text-xs font-semibold uppercase tracking-[0.08em] text-ptf-primary">{pk.label}</span>
                <p className="mt-1 text-lg font-display font-bold text-ptf-primary">₹{Number(pk.price).toLocaleString("en-IN")}</p>
                <p className="text-[0.6rem] text-ptf-text/50">GST included</p>
              </label>
            ))}
          </div>
        </div>
      )}
      {showDiscount && (
        <p data-testid="training-discount-note" className="border border-ptf-secondary/30 bg-ptf-secondary/[0.05] px-4 py-3 text-xs text-ptf-secondary leading-relaxed">
          {discountSource === "dawn" ? "As a paid DAWN applicant" : "As an active Foundation member"}, you get {discountPercent}% off this programme, reducing the fee from ₹{baseFee.toLocaleString("en-IN")} to ₹{displayFee.toLocaleString("en-IN")}. The discount is applied automatically at checkout, and is individual and non-transferable.
        </p>
      )}
      {programme.editions?.length > 0 && (
        <label className="block">
          <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-text/55">Choose month & batch</span>
          <select data-testid="reg-edition" value={editionId} onChange={(e) => setEditionId(e.target.value)} className={inputCls}>
            <option value="">Any upcoming batch</option>
            {Object.entries(
              programme.editions.reduce((groups, ed) => {
                const monthKey = ed.start_date ? new Date(ed.start_date).toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : "Date to be announced";
                (groups[monthKey] = groups[monthKey] || []).push(ed);
                return groups;
              }, {})
            ).map(([month, eds]) => (
              <optgroup key={month} label={month}>
                {eds.map((ed, i) => {
                  const cap = Number(ed.capacity) || 0;
                  const booked = Number(ed.seats_booked || 0);
                  const left = cap > 0 ? cap - booked : null;
                  const daysToStart = ed.start_date ? Math.ceil((new Date(ed.start_date) - new Date()) / 86400000) : null;
                  const cutoffClosed = daysToStart !== null && daysToStart < 15;
                  const isFull = ed.registration_status === "CLOSED" || (left !== null && left <= 0);
                  const disabled = isFull || cutoffClosed;
                  return (
                    <option key={ed.id} value={ed.id} disabled={disabled}>
                      Batch {String(i + 1).padStart(2, "0")}{left !== null ? ` · ${cap} seats · ${left} available · ${booked} confirmed` : ""}
                      {isFull ? " · Batch Full" : cutoffClosed ? " · Booking closed (within 15 days)" : ""}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </select>
        </label>
      )}
      <label className="flex items-start gap-3 text-xs text-ptf-text/70 leading-relaxed cursor-pointer">
        <input type="checkbox" data-testid="reg-volunteer-interest" checked={volunteerInterest} onChange={(e) => setVolunteerInterest(e.target.checked)} className="mt-0.5 accent-ptf-primary" />
        I would also like to explore volunteering opportunities.
      </label>
      <label className="flex items-start gap-3 text-xs text-ptf-text/70 leading-relaxed cursor-pointer">
        <input type="checkbox" data-testid="reg-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-ptf-primary" />
        I consent to Planet Trotter Foundation storing this information to process my registration and contacting me about this programme. Information is used only for programme administration. *
      </label>
      <button type="submit" data-testid="reg-submit" disabled={loading} className="w-full sm:w-auto bg-ptf-primary text-ptf-bg px-10 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {programme.status === "CUSTOM"
          ? "Request a Custom Programme"
          : programme.status !== "OFFERED"
          ? "Enquire About This Programme"
          : regStatus === "FULL"
          ? "Join the Waitlist"
          : regStatus === "CLOSED"
          ? "Enquire About the Next Edition"
          : paid
          ? `Register & Pay ₹${displayFee.toLocaleString("en-IN")}`
          : "Register"}
      </button>
    </form>
  );
}

const Row = ({ label, children, testid }) => (
  <div data-testid={testid} className="py-6 border-t border-ptf-primary/12 grid grid-cols-1 md:grid-cols-12 gap-3">
    <h2 className="md:col-span-4 font-display uppercase text-xl tracking-tight text-ptf-primary">{label}</h2>
    <div className="md:col-span-8 text-sm text-ptf-text/75 leading-relaxed">{children}</div>
  </div>
);

export default function TrainingDetail() {
  const { slug } = useParams();
  const { data: p, isLoading, isError } = useQuery({ queryKey: ["training", slug], queryFn: async () => (await api.get(`/training/${slug}`)).data });
  const { data: all = [] } = useQuery({ queryKey: ["training"], queryFn: async () => (await api.get("/training")).data });

  useEffect(() => {
    if (!p) return;
    trackMetaEvent("ViewContent", { content_name: p.title, content_type: "training", content_ids: [p.slug] });
  }, [p]);

  if (isLoading) return <div className="min-h-[60vh] grid place-items-center bg-ptf-bg"><Loader2 className="h-6 w-6 animate-spin text-ptf-primary" /></div>;
  if (isError || !p) return (
    <div className="min-h-[60vh] grid place-items-center bg-ptf-bg text-center px-6">
      <div>
        <h1 className="font-display uppercase text-3xl text-ptf-primary">Programme not found.</h1>
        <PtfButton to="/learning/training" className="mt-6">Back to the Catalogue</PtfButton>
      </div>
    </div>
  );

  const related = all.filter((x) => x.category === p.category && x.slug !== p.slug).slice(0, 3);
  const statusLabel = (STATUS_META[p.status] || {}).label || p.status;
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Course", name: p.title, description: p.description,
      provider: { "@type": "Organization", name: "Planet Trotter Foundation", url: "https://planettrotterfoundation.com" } },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Field Learning", item: "https://planettrotterfoundation.com/learning" },
      { "@type": "ListItem", position: 2, name: "Training", item: "https://planettrotterfoundation.com/learning/training" },
      { "@type": "ListItem", position: 3, name: p.title, item: `https://planettrotterfoundation.com/learning/programme/${p.slug}` },
    ] },
  ];

  return (
    <>
      <Seo title={p.seo_title || p.title} description={p.seo_description || p.description} path={`/learning/programme/${p.slug}`} image={p.image} jsonLd={jsonLd} />

      <section className="relative bg-ptf-primary text-ptf-bg grain overflow-hidden">
        <img src={p.image || "/dawn/vor-trek.jpg"} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-ptf-primary via-ptf-primary/60 to-ptf-primary/30" />
        <div className="relative mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 pt-40 pb-14 md:pb-20">
          <p className="text-[0.68rem] uppercase tracking-[0.24em] text-ptf-accent">{p.category_name}{p.subcategory ? ` · ${p.subcategory}` : ""}</p>
          <h1 data-testid="programme-title" className="mt-3 font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight max-w-4xl">{p.title}</h1>
          <div className="mt-4"><StatusBadge status={p.status} /></div>
          <p className="mt-5 max-w-2xl text-ptf-bg/80 leading-relaxed">{p.description}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="#register" data-testid="hero-register-cta" className="inline-flex items-center gap-2 bg-ptf-accent text-ptf-primary px-8 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em] hover:brightness-95 transition">
              {p.status === "OFFERED" ? "Register" : "Enquire"} <ArrowUpRight className="h-4 w-4" />
            </a>
            {p.related_expedition_ids?.length > 0 && (
              <Link
                to={`/expeditions/${p.related_expedition_ids[0]}`}
                data-testid="training-know-more-btn"
                className="inline-flex items-center gap-2 border border-ptf-bg/40 text-ptf-bg px-8 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em] hover:bg-ptf-bg hover:text-ptf-primary transition-colors"
              >
                Know More <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
        <div className="relative border-t border-ptf-bg/15">
          <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 py-4 flex flex-wrap gap-x-8 gap-y-2 text-[0.62rem] uppercase tracking-[0.14em] text-ptf-bg/70" data-testid="programme-facts">
            <span>Format · {p.format}</span>
            <span>Duration · {p.duration}</span>
            <span>Location · {p.location}</span>
            <span>Level · {p.level}</span>
            <span>Certificate · {p.certificate === "YES" ? "Yes" : p.certificate === "NO" ? "No" : "Depends on edition"}</span>
            <span>Fee · {p.fee_type === "PAID" && p.fee ? `₹${Number(p.fee).toLocaleString("en-IN")}` : p.fee_type === "FREE" ? "Free" : p.fee_type === "DONATION" ? "Donation based" : p.fee_type === "SPONSORED" ? "Sponsored" : "On enquiry"}</span>
          </div>
        </div>
      </section>

      <section className="bg-ptf-bg py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
          {p.status !== "OFFERED" && (
            <p data-testid="status-honesty-note" className="mb-8 border border-ptf-primary/15 bg-white px-4 py-3 text-xs text-ptf-text/70 leading-relaxed">
              Status: <strong>{statusLabel}</strong>.{" "}
              {p.status === "COMING_SOON" && "This programme is part of the planned catalogue and is not yet open for registration. Enquire below and we will write to you when it opens."}
              {p.status === "DELIVERED" && "This programme is supported by Planet Trotter's field documentation. New editions are announced here; enquire below to be told when the next one opens."}
              {p.status === "CUSTOM" && "This programme is designed per group. Send an enquiry and we will shape it with you."}
              {p.status === "COMPLETED" && "This edition has completed. Enquire below to hear about the next one."}
              {p.status === "FULL" && "The current edition is full. Enquire below to join the waitlist."}
            </p>
          )}

          <div className="space-y-0">
            {p.about && <Row label="About the programme" testid="section-about">{p.about}</Row>}
            {p.learn?.length > 0 && (
              <Row label="What you will learn" testid="section-learn">
                <ul className="space-y-1.5">{p.learn.map((l) => <li key={l} className="flex gap-2"><Check className="h-4 w-4 text-ptf-secondary shrink-0 mt-0.5" />{l}</li>)}</ul>
              </Row>
            )}
            {p.who_for?.length > 0 && (
              <Row label="Who it is for" testid="section-who">
                <div className="flex flex-wrap gap-2">{p.who_for.map((w) => <span key={w} className="text-[0.65rem] uppercase tracking-[0.12em] border border-ptf-primary/20 px-3 py-1.5">{w}</span>)}</div>
              </Row>
            )}
            {p.prerequisites?.length > 0 && (
              <Row label="Prerequisites" testid="section-prerequisites">
                <ul className="space-y-1.5">{p.prerequisites.map((x) => <li key={x}>{x}</li>)}</ul>
              </Row>
            )}
            {p.what_to_bring?.length > 0 && (
              <Row label="What to bring" testid="section-bring">
                <ul className="space-y-1.5">{p.what_to_bring.map((x) => <li key={x}>{x}</li>)}</ul>
              </Row>
            )}
            {p.safety?.length > 0 && (
              <Row label="Safety & eligibility" testid="section-safety">
                <ul className="space-y-2">
                  {p.safety.map((s) => <li key={s} className="flex gap-2"><ShieldCheck className="h-4 w-4 text-ptf-secondary shrink-0 mt-0.5" />{s}</li>)}
                </ul>
              </Row>
            )}
            {p.instructor && <Row label="Instructor / facilitator" testid="section-instructor">{p.instructor}</Row>}
            <Row label="Certification" testid="section-certification">
              {p.certificate === "NO"
                ? "No certificate is issued for this programme."
                : p.certificate === "YES"
                  ? `${p.certificate_type || "Planet Trotter Training Certificate"}. Every certificate carries a number verifiable at /verify.`
                  : "Certification depends on the edition and format. Where issued, it is a Planet Trotter Training Certificate with a publicly verifiable number. It is not presented as an accredited qualification unless accreditation exists for that edition."}
            </Row>
            {p.upcoming_dates?.length > 0 && (
              <Row label="Upcoming dates" testid="section-dates">
                <ul className="space-y-1.5">{p.upcoming_dates.map((d) => <li key={d}>{d}</li>)}</ul>
              </Row>
            )}
            {(p.connection || p.applied) && (
              <Row label="How it connects" testid="section-connection">
                {p.applied && <p><strong className="text-ptf-primary">Where it can be applied:</strong> {p.applied}</p>}
                {p.connection && <p className="mt-2"><strong className="text-ptf-primary">Planet Trotter connection:</strong> {p.connection}</p>}
              </Row>
            )}
            {p.documented_note && (
              <Row label="Field record" testid="section-documented">
                <p>{p.documented_note}</p>
                {p.related_stories?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-3">
                    {p.related_stories.map((s) => (
                      <Link key={s.to} to={s.to} className="inline-flex items-center gap-1 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-ptf-secondary hover:text-ptf-primary">
                        {s.label} <ArrowUpRight className="h-3.5 w-3.5" />
                      </Link>
                    ))}
                  </div>
                )}
              </Row>
            )}
            {p.faq?.length > 0 && (
              <Row label="FAQ" testid="section-faq">
                <div className="space-y-4">{p.faq.map((f) => <div key={f.q}><p className="font-semibold text-ptf-primary">{f.q}</p><p className="mt-1">{f.a}</p></div>)}</div>
              </Row>
            )}
            <Row label="Group bookings" testid="section-groups">
              Schools, corporates, NGOs, clubs and institutions can run this programme as a custom group edition.{" "}
              <Link to="/learning/institutional" className="underline underline-offset-2 text-ptf-secondary">Request a custom programme</Link>.
            </Row>
          </div>
        </div>
      </section>

      <section id="register" className="bg-white border-y border-ptf-primary/10 py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow>{p.status === "OFFERED" ? "Register" : "Enquire"}</Eyebrow></Reveal>
          <MaskText
            lines={[p.status === "OFFERED" ? "Take your place." : "Ask about this", p.status === "OFFERED" ? "" : "programme."].filter(Boolean)}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
          />
          <div className="mt-10 max-w-3xl">
            {p.slug === "the-last-horizon" ? (
              <div data-testid="last-horizon-training-cta" className="border border-ptf-primary/12 bg-white p-7">
                <p className="text-sm text-ptf-text/70 leading-relaxed mb-5">
                  The Last Horizon is entered through its own application: ten wildlife photographs, a
                  30-second video, declarations and the ₹5,000 application fee, reviewed by the Foundation jury.
                </p>
                <PtfButton to="/last-horizon/apply" data-testid="last-horizon-training-apply-btn">Apply for The Last Horizon</PtfButton>
              </div>
            ) : p.slug === "dawn-responsible-tourism-practice" ? (
              <div data-testid="dawn-training-cta" className="border border-ptf-primary/12 bg-white p-7">
                <p className="text-sm text-ptf-text/70 leading-relaxed mb-5">
                  Dawn: Project of belief is entered through its own application: 24 Expeditioners on a journey, a
                  form and a video, declarations and the ₹499 application fee, reviewed by the Foundation jury.
                </p>
                <PtfButton to="/dawn/apply" data-testid="dawn-training-apply-btn">Apply for Dawn: Project of Belief</PtfButton>
              </div>
            ) : p.status === "COMING_SOON" ? (
              <NotifyMeForm programme={p} />
            ) : (
              <RegistrationForm programme={p} />
            )}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-ptf-bg py-16 md:py-24">
          <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
            <Reveal><Eyebrow>Related programmes</Eyebrow></Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((r) => <ProgrammeCard key={r.slug} p={r} />)}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
